// P4 · Ruta (documento 07). La siguiente parada siempre a mano; el resto, en orden.
import { BarraProgreso, Boton, Chip, cx, Estadistica, Icono, Tarjeta, type TonoChip } from '@reparto/ui';
import {
  enlaceNavegar,
  formatoGs,
  formatoHora,
  MOTIVO_NO_ENTREGA_ETIQUETA,
  type ResultadoParada,
} from '@reparto/shared/nucleo';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { optimizar } from '../datos/acciones';
import { useEstado } from '../datos/ganchos';
import { destinoParada } from '../datos/vista';
import { EsqueletoRuta } from '../componentes/Esqueletos';
import { Marco } from '../componentes/Marco';
import { PanelMapa } from '../componentes/PanelMapa';
import { ventana } from '../componentes/formato';
import { usePantallaAncha } from '../componentes/pantalla';
import { gps } from '../servicios/gps';

const RESULTADO: Record<Exclude<ResultadoParada, 'pendiente'>, { texto: string; tono: TonoChip; icono: string }> = {
  entregada: { texto: 'Entregada', tono: 'exito', icono: 'check_circle' },
  parcial: { texto: 'Parcial', tono: 'aviso', icono: 'incomplete_circle' },
  no_entregada: { texto: 'No entregada', tono: 'peligro', icono: 'cancel' },
};

export function RutaDelDia() {
  const navegar = useNavigate();
  const { vista } = useEstado();
  const [permisoGps, setPermisoGps] = useState<string>('concedido');
  // En pantallas anchas el mapa queda a la vista al lado de la lista; en el celular se abre aparte.
  const ancha = usePantallaAncha();
  useEffect(() => {
    void gps.permiso().then(setPermisoGps);
  }, []);
  const ruta = vista?.ruta;
  if (!vista || !ruta) return <EsqueletoRuta />;
  const siguiente = ruta.paradas.find((p) => p.resultado === 'pendiente');
  const quedan = ruta.paradas.filter((p) => p.resultado === 'pendiente').length;
  const hechas = ruta.paradas.length - quedan;
  const bultosEntregados = ruta.paradas.reduce(
    (s, p) => s + p.items.reduce((t, i) => t + (i.bultos_entregados ?? 0), 0),
    0,
  );
  const reoptimizar = vista.configuracion.ruta.reoptimizar_en_curso && quedan > 2;

  const pie = siguiente ? (
    <div className="grid grid-cols-[1fr_auto] gap-2 pb-1">
      <Boton tamano="lg" icono="arrow_forward" onClick={() => navegar(`/parada/${siguiente.id}`)} className="min-w-0">
        <span className="truncate">Siguiente: {siguiente.cliente.nombre}</span>
      </Boton>
      <Boton
        tamano="lg"
        variante="secundario"
        icono="navigation"
        aria-label="Navegar"
        onClick={() =>
          window.open(enlaceNavegar(destinoParada(siguiente, ruta.deposito.ubicacion)), '_blank', 'noopener')
        }
      >
        Ir
      </Boton>
    </div>
  ) : (
    <Boton bloque tamano="lg" variante="acento" icono="receipt_long" onClick={() => navegar('/cierre')}>
      Cerrar ruta y rendir
    </Boton>
  );

  return (
    <Marco
      titulo="Ruta del día"
      subtitulo={`${vista.movil.nombre} · salida ${ruta.salida_en ? formatoHora(ruta.salida_en) : '—'}`}
      paso="entrega"
      pie={pie}
      lateral={ancha ? <PanelMapa ruta={ruta} /> : undefined}
    >
      <div className="space-y-3 p-4">
        <Tarjeta className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Estadistica valor={`${hechas}/${ruta.paradas.length}`} etiqueta="paradas" />
            <Estadistica valor={`${bultosEntregados}/${ruta.resumen.bultos}`} etiqueta="bultos" />
            <Estadistica valor={formatoGs(ruta.resumen.cobrado).replace('Gs. ', '')} etiqueta="cobrado Gs." />
          </div>
          <BarraProgreso valor={ruta.paradas.length ? hechas / ruta.paradas.length : 0} />
          <div className="flex items-center justify-between text-xs text-stone-500 dark:text-stone-400">
            <span>
              {quedan
                ? `Quedan ${quedan} · vuelta aprox. ${ruta.resumen.hora_estimada_fin ?? '—'}`
                : 'Todas las paradas resueltas'}
            </span>
            {ancha ? null : (
              <button
                type="button"
                onClick={() => navegar('/mapa')}
                className="flex items-center gap-1 font-medium text-primary-700 dark:text-primary-300"
              >
                <Icono nombre="map" tamano={18} /> Mapa
              </button>
            )}
          </div>
        </Tarjeta>

        {permisoGps === 'denegado' ? (
          <p className="flex gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
            <Icono nombre="location_off" className="shrink-0" /> La ubicación está bloqueada. La app funciona igual,
            pero el depósito no ve dónde estás. Activala en los ajustes del navegador.
          </p>
        ) : null}

        {reoptimizar ? (
          <Boton bloque variante="secundario" icono="auto_awesome" onClick={() => void optimizar(ruta.id)}>
            Reordenar lo que falta
          </Boton>
        ) : null}

        <ol className="space-y-2">
          {ruta.paradas.map((p) => {
            const es = p.id === siguiente?.id;
            const r = p.resultado !== 'pendiente' ? RESULTADO[p.resultado] : null;
            const v = ventana(p.pedido.ventana_desde, p.pedido.ventana_hasta);
            return (
              <li key={p.id}>
                <button type="button" onClick={() => navegar(`/parada/${p.id}`)} className="block w-full text-left">
                  <Tarjeta relleno="sm" resaltada={es} className={cx('flex items-center gap-3', r && 'opacity-80')}>
                    <span
                      className={cx(
                        'grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold tabular-nums',
                        r
                          ? 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300'
                          : es
                            ? 'bg-amber-400 text-stone-900'
                            : 'bg-primary-700 text-white',
                      )}
                    >
                      {r ? <Icono nombre={r.icono} tamano={20} /> : p.orden}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{p.cliente.nombre}</span>
                        {p.pendiente_sync ? (
                          <Icono nombre="cloud_upload" tamano={16} className="text-amber-600" aria-label="Sin enviar" />
                        ) : null}
                      </div>
                      <div className="truncate text-xs text-stone-500 dark:text-stone-400">
                        {p.cliente.zona_nombre ?? 'Sin zona'} · {p.pedido.bultos_total} bultos
                        {p.pedido.cobro === 'cuenta_corriente'
                          ? ' · cuenta corriente'
                          : ` · ${formatoGs(p.pedido.monto)}`}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r ? (
                          <Chip tono={r.tono}>
                            {r.texto}
                            {p.resuelta_en ? ` ${formatoHora(p.resuelta_en)}` : ''}
                            {p.motivo ? ` · ${MOTIVO_NO_ENTREGA_ETIQUETA[p.motivo]}` : ''}
                          </Chip>
                        ) : (
                          <>
                            {p.llegada_estimada ? <Chip icono="schedule">≈ {p.llegada_estimada}</Chip> : null}
                            {v ? (
                              <Chip
                                tono={p.ventana_en_riesgo ? 'peligro' : 'neutro'}
                                icono={p.ventana_en_riesgo ? 'alarm' : 'event_available'}
                              >
                                {v}
                              </Chip>
                            ) : null}
                          </>
                        )}
                      </div>
                    </div>
                    <Icono nombre="chevron_right" className="text-stone-400" />
                  </Tarjeta>
                </button>
              </li>
            );
          })}
        </ol>

        {siguiente ? (
          <button
            type="button"
            onClick={() => navegar('/cierre')}
            className="mx-auto block py-3 text-sm text-stone-500 underline"
          >
            Terminar la ruta antes
          </button>
        ) : null}
      </div>
    </Marco>
  );
}
